import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  ApiError,
  api,
  asVerifiedProject,
  isActiveScanStatus,
  isProjectVerified,
  normalizeApiText,
  normalizeScanStatus,
  type Project,
  type Scan,
  type ScanListItem,
  type SubscriptionInfo,
} from "./api";
import { useAuth } from "./AuthContext";
import { AppContext, type AppValue, type ScanGraph } from "./AppStateContext";
import { useDataChanged } from "./useDataChanged";
import { demoStore } from "./demo/demoStore";

// Persist the live pipeline graph across page reloads. On refresh the graph
// starts null, so getScanStages can't compute honest per-stage progress and
// briefly falls back to the flat "all bars = overall %" approximation — which,
// for a running scan whose progress_percent is near 100, gets clamped to a
// uniform 99% and then visibly drops once the fresh graph arrives. Seeding
// from this cache makes the first render show the last real per-stage values.
const GRAPH_CACHE_KEY = "ces.scanGraph";

function readCachedGraph(): ScanGraph | null {
  try {
    const raw = sessionStorage.getItem(GRAPH_CACHE_KEY);
    return raw ? (JSON.parse(raw) as ScanGraph) : null;
  } catch {
    return null;
  }
}

function writeCachedGraph(graph: ScanGraph | null): void {
  try {
    if (graph) sessionStorage.setItem(GRAPH_CACHE_KEY, JSON.stringify(graph));
    else sessionStorage.removeItem(GRAPH_CACHE_KEY);
  } catch {
    /* storage unavailable / quota — caching is best-effort */
  }
}

// Pause bookkeeping persisted across reloads. localStorage (not sessionStorage)
// so it survives a full reload AND tab close. Since `activePause.since` is an
// absolute timestamp, resuming after a reload still banks the whole paused span,
// reload gap included.
const PAUSE_STATE_KEY = "ces.scanPause";

type PauseState = {
  pausedMsByScan: Record<string, number>;
  activePause: { scanId: string; since: number } | null;
};

function readPauseState(): PauseState {
  try {
    const raw = localStorage.getItem(PAUSE_STATE_KEY);
    if (!raw) return { pausedMsByScan: {}, activePause: null };
    const parsed = JSON.parse(raw) as Partial<PauseState>;
    return {
      pausedMsByScan: parsed.pausedMsByScan ?? {},
      activePause: parsed.activePause ?? null,
    };
  } catch {
    return { pausedMsByScan: {}, activePause: null };
  }
}

function writePauseState(state: PauseState): void {
  try {
    const isEmpty = !state.activePause && Object.keys(state.pausedMsByScan).length === 0;
    if (isEmpty) localStorage.removeItem(PAUSE_STATE_KEY);
    else localStorage.setItem(PAUSE_STATE_KEY, JSON.stringify(state));
  } catch {
    /* storage unavailable / quota — best-effort */
  }
}

export function AppStateProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [rawProjects, setProjects] = useState<Project[]>([]);
  // Projects whose targets we've seen verified ourselves. isProjectVerified()
  // reads the project's verified_targets/total_scope_targets counters, and the
  // backend updates those asynchronously — a GET /projects fired right after a
  // successful /verification/complete can still report the old numbers, which
  // made the app treat the project as unverified and drop the user back into
  // onboarding. Target-level truth wins until the counters catch up.
  const [locallyVerified, setLocallyVerified] = useState<ReadonlySet<string>>(
    () => new Set<string>(),
  );
  const [activeScan, setActiveScan] = useState<Scan | null>(null);
  // Hydrate from the cached graph so a refresh mid-scan renders the last real
  // per-stage progress instead of the transient uniform 99%. reload() restores
  // it again once it knows which scan is active (the poll effect nulls it
  // before reload finishes), and the poll tick overwrites it with fresh data.
  const [activeScanGraph, setActiveScanGraph] = useState<ScanGraph | null>(readCachedGraph);
  // Pause bookkeeping for the active-time scan timer (see useScanElapsed).
  // Recorded straight from the pause/resume actions (exact click time, no poll
  // lag) and persisted to localStorage so a reload mid-pause is still counted.
  // Client-side only: the backend exposes no paused-duration field, so this
  // doesn't span devices or survive clearing site data.
  const [pausedMsByScan, setPausedMsByScan] = useState<Record<string, number>>(
    () => readPauseState().pausedMsByScan,
  );
  const [activePause, setActivePause] = useState<PauseState["activePause"]>(
    () => readPauseState().activePause,
  );
  const [viewedScanIdState, setViewedScanIdState] = useState<string | null>(null);
  const [viewedScanOverride, setViewedScanOverride] = useState<Scan | null>(null);
  const [projectScans, setProjectScans] = useState<Record<string, ScanListItem[] | undefined>>({});
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [noPlan, setNoPlan] = useState(false);
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  const [loading, setLoading] = useState(Boolean(user));
  const [initialized, setInitialized] = useState(!user);
  const [error, setError] = useState<string | null>(null);
  const [scanIntroToken, setScanIntroToken] = useState(0);
  const activeScanId = activeScan?.scan_id ?? null;
  // Synchronous lock so rapid clicks on a start-scan button (or two different
  // start buttons) can't fire api.startScan more than once — that was launching
  // 3 scans from 3 impatient clicks before any visual feedback appeared.
  const startingScanRef = useRef(false);

  const reload = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      // Backend has no /_ui/state composite — fan out and merge.
      const [projectsResp, subOutcome, scansResp] = await Promise.all([
        api.listProjects(),
        // Wrap subscription so we can distinguish 412 (no plan) from other errors.
        api
          .subscription()
          .then((data) => ({ ok: true as const, data }))
          .catch((err: unknown) => ({ ok: false as const, err })),
        api.listScans().catch(() => null),
      ]);
      setProjects(projectsResp.projects);
      if (subOutcome.ok) {
        setSubscription(subOutcome.data);
        setNoPlan(!subOutcome.data.plan);
      } else {
        setSubscription(null);
        // FAILED_PRECONDITION = backend explicitly says "no plan yet".
        const isNoPlan =
          subOutcome.err instanceof ApiError &&
          (subOutcome.err.status === 412 ||
            subOutcome.err.code === "FAILED_PRECONDITION");
        setNoPlan(isNoPlan);
      }
      // ScanListItem omits project_id, so fetch full ScanInfo for the
      // active scan to know which project it belongs to.
      const runningItem = scansResp?.scans.find((s) => isActiveScanStatus(s.status));
      const running = runningItem ? await api.getScan(runningItem.scan_id).catch(() => null) : null;
      setActiveScan(running ?? null);
      // Restore the cached graph for the scan we're about to display so the
      // first render shows real per-stage progress (the poll tick refreshes it
      // a moment later). Only when it belongs to this scan — a stale graph for a
      // different scan would be ignored by getScanStages anyway, but don't seed
      // it. When there's no running scan, drop the cache.
      if (running) {
        const cached = readCachedGraph();
        if (cached && cached.scan_id === running.scan_id) setActiveScanGraph(cached);
      } else {
        setActiveScanGraph(null);
        writeCachedGraph(null);
      }
      setActiveProjectId((prev) => {
        if (prev && projectsResp.projects.some((p) => p.project_id === prev)) return prev;
        return running?.project_id ?? projectsResp.projects[0]?.project_id ?? null;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, [user]);

  const resetState = useCallback(() => {
    setProjects([]);
    setActiveScan(null);
    setActiveScanGraph(null);
    setPausedMsByScan({});
    setActivePause(null);
    writeCachedGraph(null);
    setViewedScanIdState(null);
    setViewedScanOverride(null);
    setProjectScans({});
    setSubscription(null);
    setNoPlan(false);
    setActiveProjectId(null);
    setLoading(false);
    setInitialized(true);
    setError(null);
  }, []);

  const loadProjectScans = useCallback(async (projectId: string) => {
    try {
      const res = await api.listScans({ project_id: projectId });
      setProjectScans((prev) => ({ ...prev, [projectId]: res.scans }));
    } catch {
      setProjectScans((prev) => ({ ...prev, [projectId]: prev[projectId] ?? [] }));
    }
  }, []);

  const setViewedScanId = useCallback((id: string | null) => {
    setViewedScanIdState(id);
    if (!id) {
      setViewedScanOverride(null);
      return;
    }
    if (activeScan?.scan_id === id) {
      setViewedScanOverride(null);
      return;
    }
    api
      .getScan(id)
      .then((fresh) => setViewedScanOverride(fresh))
      .catch(() => setViewedScanOverride(null));
  }, [activeScan?.scan_id]);

  const viewedScan = useMemo(() => {
    if (viewedScanIdState && viewedScanOverride && viewedScanOverride.scan_id === viewedScanIdState) {
      return viewedScanOverride;
    }
    return activeScan;
  }, [viewedScanIdState, viewedScanOverride, activeScan]);

  // The scan actually rendered on screen — graph, ScanBar, ScanStrip and
  // ChatPanel all follow this. Defaults to the active scan when nothing is
  // pinned. (A string, so the poll effect below doesn't re-run on every tick.)
  const displayedScanId = viewedScan?.scan_id ?? null;

  // Merge a fresh ScanInfo into whichever slot currently holds that scan — the
  // active scan and/or the viewed override — so polling and pause/cancel keep
  // the displayed scan live no matter which one is selected.
  const applyScanUpdate = useCallback((info: Scan) => {
    setActiveScan((cur) => (cur?.scan_id === info.scan_id ? info : cur));
    setViewedScanOverride((cur) => (cur?.scan_id === info.scan_id ? info : cur));
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!user) {
      const resetId = window.setTimeout(() => {
        if (!cancelled) resetState();
      }, 0);
      return () => {
        cancelled = true;
        window.clearTimeout(resetId);
      };
    }
    setInitialized(false);
    setLoading(true);
    const loadId = window.setTimeout(() => void reload(), 0);
    return () => {
      cancelled = true;
      window.clearTimeout(loadId);
    };
  }, [user, reload, resetState]);

  // Poll active scan every 5s so progress_percent + current_node_id stay fresh.
  // /scans/{id} now reports everything (no separate /progress endpoint).
  // Fetch /scans/{id}/graph in parallel — ScanBar uses per-node statuses from
  // the graph to compute honest per-stage progress instead of approximating
  // from the aggregate progress_percent.
  // Poll the *displayed* scan (viewedScan) every 5s for fresh info + graph. The
  // graph drives the per-stage ScanBar, so it must belong to the scan on screen
  // — otherwise getScanStages can't match it (graph.scan_id ≠ shown scan) and
  // silently falls back to the flat "all bars = overall %" approximation. That
  // mismatch is exactly what surfaced when two scans run and the user views the
  // one that isn't the active (first-running) scan.
  useEffect(() => {
    if (!displayedScanId) {
      setActiveScanGraph(null);
      return;
    }
    const tick = async () => {
      const [infoResult, graphResult] = await Promise.allSettled([
        api.getScan(displayedScanId),
        api.scanGraph(displayedScanId),
      ]);
      if (infoResult.status === "fulfilled") applyScanUpdate(infoResult.value);
      if (graphResult.status === "fulfilled") setActiveScanGraph(graphResult.value);
      /* network errors are surfaced elsewhere */
    };
    void tick();
    // The interactive demo advances in-browser and wants a snappier graph —
    // poll every second instead of five while it runs.
    const id = window.setInterval(tick, demoStore.active ? 1000 : 5000);
    return () => window.clearInterval(id);
  }, [displayedScanId, applyScanUpdate]);

  // The demo mutates its sample data in the browser; refetch on every change so
  // screens update at once. Outside the demo the event never fires.
  useDataChanged(reload);

  // Mirror the live graph into sessionStorage so the next reload can seed from
  // it. Only persist non-null values — the transient null the poll effect sets
  // before reload finishes must not wipe a still-valid cache.
  useEffect(() => {
    if (activeScanGraph) writeCachedGraph(activeScanGraph);
  }, [activeScanGraph]);

  // Persist pause bookkeeping so a reload mid-pause keeps counting it.
  useEffect(() => {
    writePauseState({ pausedMsByScan, activePause });
  }, [pausedMsByScan, activePause]);

  const markProjectVerified = useCallback((projectId: string) => {
    setLocallyVerified((prev) => {
      if (prev.has(projectId)) return prev;
      const next = new Set(prev);
      next.add(projectId);
      return next;
    });
  }, []);

  const projects = useMemo(() => {
    if (locallyVerified.size === 0) return rawProjects;
    return rawProjects.map((p) =>
      locallyVerified.has(p.project_id) ? asVerifiedProject(p) : p,
    );
  }, [rawProjects, locallyVerified]);

  // Retire an override as soon as the server agrees (or the project is gone).
  // Left in place it would keep masking a project that later gains a new,
  // genuinely unverified target.
  useEffect(() => {
    if (locallyVerified.size === 0) return;
    const stale = [...locallyVerified].filter((id) => {
      const p = rawProjects.find((x) => x.project_id === id);
      return !p || isProjectVerified(p);
    });
    if (stale.length === 0) return;
    setLocallyVerified((prev) => {
      const next = new Set(prev);
      for (const id of stale) next.delete(id);
      return next;
    });
  }, [rawProjects, locallyVerified]);

  const activeProject = useMemo(
    () => projects.find((p) => p.project_id === activeProjectId) ?? null,
    [projects, activeProjectId],
  );

  const createProject = useCallback(
    async ({
      name,
      description,
      jurisdiction_code,
    }: {
      name: string;
      description?: string;
      jurisdiction_code?: string;
    }) => {
      const project = await api.createProject({ name, description, jurisdiction_code });
      setProjects((prev) => [...prev, project]);
      setActiveProjectId(project.project_id);
      // A brand-new project owns no scans yet; any viewedScan still pinned
      // from a previous project would otherwise render under the new
      // project's name on /overview after onboarding completes.
      setViewedScanIdState(null);
      setViewedScanOverride(null);
      return project;
    },
    [],
  );

  const deleteProject = useCallback(async (id: string) => {
    await api.deleteProject(id);
    setProjects((prev) => prev.filter((p) => p.project_id !== id));
    setActiveProjectId((prev) => (prev === id ? null : prev));
    setProjectScans((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const updateProjectJurisdiction = useCallback(
    async (projectId: string, code: string) => {
      const updated = await api.updateProject(projectId, { jurisdiction_code: code });
      setProjects((prev) =>
        prev.map((p) => (p.project_id === projectId ? updated : p)),
      );
      return updated;
    },
    [],
  );

  // pause/resume return only {scan_id, status}, so re-fetch the full ScanInfo
  // to keep the displayed scan complete (phase, progress, counts).
  const refetchScan = useCallback(async (id: string) => {
    try {
      applyScanUpdate(await api.getScan(id));
    } catch {
      /* ignore */
    }
  }, [applyScanUpdate]);

  // Refetch the billing subscription so the scan-quota counter reflects usage
  // the backend changed during a scan's lifecycle. Only reload() fetches it
  // otherwise, so without this the counter stays stale until a full page reload.
  // Keep the last value on transient errors; only blank it on an explicit
  // "no plan" (412 / FAILED_PRECONDITION).
  const refreshSubscription = useCallback(async () => {
    try {
      const data = await api.subscription();
      setSubscription(data);
      setNoPlan(!data.plan);
    } catch (err) {
      if (err instanceof ApiError && (err.status === 412 || err.code === "FAILED_PRECONDITION")) {
        setSubscription(null);
        setNoPlan(true);
      }
    }
  }, []);

  // When the active scan ends, the backend has adjusted the monthly scan quota —
  // refetch the subscription so the counter updates without a manual reload.
  useEffect(() => {
    const st = normalizeScanStatus(activeScan?.status);
    if (st !== "completed" && st !== "failed" && st !== "cancelled") return;
    const id = window.setTimeout(() => void refreshSubscription(), 0);
    return () => window.clearTimeout(id);
  }, [activeScan?.scan_id, activeScan?.status, refreshSubscription]);

  const pauseScan = useCallback(async (scanId?: string) => {
    const id = scanId ?? activeScanId;
    if (!id) return;
    // Open a pause interval at click time so the countdown can discount it.
    setActivePause({ scanId: id, since: Date.now() });
    await api.pauseScan(id);
    await refetchScan(id);
  }, [activeScanId, refetchScan]);

  const resumeScan = useCallback(async (scanId?: string) => {
    const id = scanId ?? activeScanId;
    if (!id) return;
    // Close the open pause interval and bank its duration.
    if (activePause && activePause.scanId === id) {
      const delta = Date.now() - activePause.since;
      if (delta > 0) {
        setPausedMsByScan((m) => ({ ...m, [id]: (m[id] ?? 0) + delta }));
      }
    }
    setActivePause(null);
    await api.resumeScan(id);
    await refetchScan(id);
  }, [activeScanId, refetchScan, activePause]);

  const cancelScan = useCallback(async (scanId?: string) => {
    const id = scanId ?? activeScanId;
    if (!id) return;
    const projectId =
      (id === activeScan?.scan_id ? activeScan?.project_id : viewedScanOverride?.project_id) ?? null;
    await api.cancelScan(id);
    setActivePause((p) => (p && p.scanId === id ? null : p));
    setActiveScan((cur) => (cur?.scan_id === id ? null : cur));
    setViewedScanOverride((cur) => (cur?.scan_id === id ? null : cur));
    setViewedScanIdState((cur) => (cur === id ? null : cur));
    if (projectId) void loadProjectScans(projectId);
    void refreshSubscription();
  }, [activeScanId, activeScan, viewedScanOverride, loadProjectScans, refreshSubscription]);

  const startScan = useCallback(
    async (targetId: string) => {
      // Re-entrancy lock — a second concurrent start is a no-op, not a 2nd scan.
      if (startingScanRef.current) return null;
      startingScanRef.current = true;
      try {
        const started = await api.startScan(targetId);
        const fresh = await api.getScan(started.scan_id).catch(() => null);
        setActiveScan(fresh);
        setViewedScanIdState(null);
        setViewedScanOverride(null);
        setScanIntroToken((n) => n + 1);
        if (fresh?.project_id) void loadProjectScans(fresh.project_id);
        void refreshSubscription();
        return fresh;
      } finally {
        startingScanRef.current = false;
      }
    },
    [loadProjectScans, refreshSubscription],
  );

  const startScanForProject = useCallback(
    async (projectId: string) => {
      // Re-entrancy lock — see startScan.
      if (startingScanRef.current) return null;
      startingScanRef.current = true;
      try {
        const { targets } = await api.listScopeTargets(projectId);
        const verified = targets.find(
          (t) => normalizeApiText(t.verification_status) === "verified",
        );
        if (!verified) {
          throw new Error(
            "No verified targets in this project. Verify a target to start a scan.",
          );
        }
        const started = await api.startScan(verified.target_id);
        const fresh = await api.getScan(started.scan_id).catch(() => null);
        setActiveScan(fresh);
        setActiveProjectId(projectId);
        setViewedScanIdState(null);
        setViewedScanOverride(null);
        setScanIntroToken((n) => n + 1);
        void loadProjectScans(projectId);
        void refreshSubscription();
        return fresh;
      } finally {
        startingScanRef.current = false;
      }
    },
    [loadProjectScans, refreshSubscription],
  );

  // Wrapper around the raw setter: explicit project switches (clicking a
  // project pill, finishing onboarding) should drop any historical scan the
  // user was viewing from a *different* project, otherwise /overview opens
  // showing scan data from the previous project. Callers that *do* want to
  // pin a specific scan (e.g. handleSelectScan) call setViewedScanId right
  // after — React batches both state updates so the scan-set wins.
  const setActiveProjectIdPublic = useCallback(
    (id: string) => {
      setActiveProjectId((prev) => {
        if (prev !== id) {
          setViewedScanIdState(null);
          setViewedScanOverride(null);
        }
        return id;
      });
    },
    [],
  );

  const value = useMemo<AppValue>(
    () => ({
      projects,
      activeProject,
      setActiveProjectId: setActiveProjectIdPublic,
      activeScan,
      activeScanGraph,
      pausedMsByScan,
      activePause,
      viewedScan,
      viewedScanId: viewedScanIdState,
      setViewedScanId,
      projectScans,
      loadProjectScans,
      subscription,
      noPlan,
      loading,
      initialized,
      error,
      reload,
      refreshSubscription,
      createProject,
      deleteProject,
      updateProjectJurisdiction,
      markProjectVerified,
      pauseScan,
      resumeScan,
      cancelScan,
      startScan,
      startScanForProject,
      scanIntroToken,
    }),
    [
      projects,
      activeProject,
      setActiveProjectIdPublic,
      activeScan,
      activeScanGraph,
      pausedMsByScan,
      activePause,
      viewedScan,
      viewedScanIdState,
      setViewedScanId,
      projectScans,
      loadProjectScans,
      subscription,
      noPlan,
      loading,
      initialized,
      error,
      reload,
      refreshSubscription,
      createProject,
      deleteProject,
      updateProjectJurisdiction,
      markProjectVerified,
      pauseScan,
      resumeScan,
      cancelScan,
      startScan,
      startScanForProject,
      scanIntroToken,
    ],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
